import { useMemo } from 'react';
import type { Device } from '@weave/schema-gen-ts/dist/schemas/phone/callgroup/v1/callgroup.pb';
import { Device as QueueDevice } from '@weave/schema-gen-ts/dist/schemas/phone/callqueue-config/v1/callqueue_config.pb';
import { CallGroupApi } from '@frontend/api-call-group';
import CallQueueAPI from '@frontend/api-call-queue';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useScopedQuery } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { SurfaceHeader } from '@frontend/surface-header';
import { theme } from '@frontend/theme';
import { Table, Tray, Text, Truncated, TextLink } from '@frontend/design-system';
import type { EntityType } from './types';

export const DevicePreviewModal = ({
  entityType,
  modalState,
  onClose,
}: {
  entityType: EntityType;
  modalState: {
    name: string;
    id: string;
  };
  onClose: () => void;
}) => {
  const { id, name } = modalState;
  const { navigate } = useSettingsNavigate();
  const { t } = useTranslation('analytics-phone');

  const { data: callGroupDevices, isLoading: callGroupDevicesLoading } = useScopedQuery({
    queryKey: ['phone', 'callGroup', id],
    queryFn: () =>
      CallGroupApi.readCallGroup({
        callGroupId: id,
      }),
    select: (data) => data.devices,
    staleTime: 0,
    retry: false,
    enabled: entityType === 'Group',
  });

  const { data: callQueueDevices, isLoading: callQueueDevicesLoading } = useScopedQuery({
    queryKey: ['phone-settings', 'call-queue', id],
    queryFn: () => CallQueueAPI.Read({ callQueueId: id }),
    select: (data) => data.devices,
    staleTime: 0,
    retry: false,
    enabled: entityType === 'Queue',
  });

  const dataToShow = entityType === 'Group' ? (callGroupDevices as Device[]) : (callQueueDevices as QueueDevice[]);

  const handleNameClick = () => {
    if (entityType === 'Group') navigate({ to: '/phone/call-groups/:id', params: { id } });
    else navigate({ to: '/phone/call-queues/:id', params: { id } });
    onClose();
  };

  const colConfig = useMemo(() => {
    return [
      {
        id: 'devices',
        Header: t('Assigned Device'),
        accessor: (row: Device | QueueDevice) => row,
        cellRenderer: (row: Device | QueueDevice) => {
          if ('deskPhone' in row && row.deskPhone?.name) return row.deskPhone.name;
          if ('softPhone' in row && row.softPhone?.name) return row.softPhone.name;
          if ('mobilePhone' in row && row.mobilePhone?.name) return row.mobilePhone.name;
          if ('forwardingNumber' in row && row.forwardingNumber?.name) return row.forwardingNumber.name;
          if ('name' in row) return row.name;
          return '';
        },
      },
    ];
  }, [t]);

  return (
    <>
      <SurfaceHeader.Root noBorder css={{ margin: theme.spacing(-4, -4, -2, -4), width: 'calc(100% + 64px)' }}>
        <SurfaceHeader.Row>
          <SurfaceHeader.Title>{t('Devices')}</SurfaceHeader.Title>
          <SurfaceHeader.Actions>
            <SurfaceHeader.Icon iconName='x' onClick={onClose} />
          </SurfaceHeader.Actions>
        </SurfaceHeader.Row>
        <SurfaceHeader.Body css={{ display: 'flex', flexDirection: 'row' }}>
          <Text as='div' size='medium' css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
            <Icon name={entityType === 'Group' ? 'call-group' : 'call-queue'} />
            <Text size='medium'>{t('Call {{ entityType }}:', { entityType })}</Text>
            <TextLink onClick={handleNameClick}>
              <Truncated as='span'>{name}</Truncated>
            </TextLink>
          </Text>
        </SurfaceHeader.Body>
      </SurfaceHeader.Root>
      <Tray.Body>
        <Table
          data={(dataToShow ?? []) as Device[]}
          isLoading={callGroupDevicesLoading || callQueueDevicesLoading}
          colConfig={colConfig}
        />
      </Tray.Body>
    </>
  );
};
